import { useTranslation } from 'react-i18next';
import { FUNCTIONAL_GROUPS } from '@peruselab/structure-editor/chemistry/functionalGroups';
import type { EditorTool } from '@peruselab/structure-editor/domain/editor';

interface FunctionalGroupsGroupProps {
  readonly tool: EditorTool;
  readonly setTool: (tool: EditorTool) => void;
  // #413 -- the rail disclosures are a single-open accordion owned by ToolRail.
  readonly open: boolean;
  readonly onToggle: () => void;
  /** Why the last group couldn't be placed, if it couldn't. */
  readonly error?: string | null;
}

// #736 step 1 -- the left-rail Groups disclosure: a functional group written as text (CF₃, CO₂H,
// OMe, …) arms `group-${key}`; clicking an atom puts the group's real atoms there (replacing a
// terminal atom, else bonded on), clicking empty paper places it alone.
export function FunctionalGroupsGroup({ tool, setTool, open, onToggle, error }: FunctionalGroupsGroupProps) {
  const { t } = useTranslation();
  return (
    <details id="rail-group-groups" className="workspace-rail-cycles workspace-rail-groups" open={open}>
      <summary
        onClick={(event) => {
          event.preventDefault(); // ToolRail owns which disclosure is open
          onToggle();
        }}
      >
        {t('workspace.rail.functionalGroups.groupLabel')}
      </summary>
      <div
        className="workspace-rail-cycles-body"
        role="group"
        aria-label={t('workspace.rail.functionalGroups.controlsLabel')}
      >
        <p className="rail-hint">{t('workspace.rail.functionalGroups.hint')}</p>
        <div className="workspace-rail-cycles-grid">
          {FUNCTIONAL_GROUPS.map((group) => (
            <button
              key={group.key}
              type="button"
              className={`ring-tool-button group-tool-button ${tool === `group-${group.key}` ? 'active' : ''}`}
              aria-label={t('workspace.rail.functionalGroups.arm', { name: group.name })}
              title={group.name}
              aria-pressed={tool === `group-${group.key}`}
              onClick={() => setTool(`group-${group.key}`)}
            >
              {group.label}
            </button>
          ))}
        </div>
        {error ? <p role="alert">{error}</p> : null}
      </div>
    </details>
  );
}
